import { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "react-image-gallery/styles/css/image-gallery.css";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Loader from "../../Components/Common/Loader";
import { usePublicity, useUpdatePublicity } from "../../hooks/usePublicities";
import defaultPublicityImage from "../../assets/images/lcbtp.png";
import { normalizeBackendFileUrl } from "../../utils/backendApi";
import {
  Card,
  Col,
  Container,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

// Redux
import { Link, useHistory, useParams } from "react-router-dom";

//formik
import { useFormik } from "formik";
import * as Yup from "yup";

// Import React FilePond
import { registerPlugin } from "react-filepond";

// Import FilePond styles
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

// Register the plugins
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const PublicitiesUpdate = (props) => {
  document.title = "Modifier un article";
  const [selectedFile, setSelectedFile] = useState(null);
  // Explicit removal of the stored image: without this flag, "no file sent"
  // means "keep the image", so removing it was impossible.
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file || null);
    if (file) setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setRemoveImage(true);
    // Clear the file input, which would otherwise still read as filled.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const { id } = useParams();
  const history = useHistory();

  const { data: selectAticle, isLoading, isError, error } = usePublicity(id);

  const updateMutation = useUpdatePublicity();

  // Preview: a newly picked image wins over the stored one, and nothing is
  // shown if the admin just removed it.
  const objectUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile]
  );

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const previewSrc = objectUrl
    ? objectUrl
    : removeImage
      ? null
      : normalizeBackendFileUrl(selectAticle?.publicity_pic) || null;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      publicity_pic: selectAticle?.publicity_pic || "",
      title: selectAticle?.title || "",
      publicity_content: selectAticle?.publicity_content || "",
      // `?? 1` keeps the number input from flipping uncontrolled ->
      // controlled, and stops priority from being sent as "undefined" (which
      // the API silently rejects).
      priority: selectAticle?.priority ?? 1,
    },

    validationSchema: Yup.object().shape({
      title: Yup.string().required("Ce champ est requis"),
      publicity_content: Yup.string().required("Ce champ est requis"),
      priority: Yup.number()
        .typeError("La priorité doit être un nombre")
        .integer("La priorité doit être un nombre entier")
        .min(1, "La priorité doit être supérieure ou égale à 1")
        .required("Ce champ est requis"),
    }),

    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("publicity_content", values.publicity_content);
      formData.append("priority", String(Number(values.priority)));
      if (selectedFile) {
        formData.append("publicity_pic", selectedFile);
      } else if (removeImage) {
        formData.append("remove_publicity_pic", "true");
      }

      updateMutation.mutate(
        { id, payload: formData },
        {
          onSuccess: () => {
            validation.resetForm();
            history.push("/advertisements");
          },
        }
      );
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return (
      <Loader error={error?.message || props.t("Erreur lors du chargement")} />
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Modification de publicité")}
          pageTitle={props.t("Publicité")}
        />
        <Form
          encType="multipart/form-data"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Col lg={12}>
              <Card className="p-4">
                <div className="mb-3">
                  <Label className="form-label" htmlFor="photo">
                    {props.t("IMAGE")}{" "}
                    <span className="text-muted">
                      ({props.t("facultatif")}) —{" "}
                      {props.t(
                        "laissez vide pour conserver l'image actuelle"
                      )}
                    </span>
                  </Label>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      name="publicity_pic"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                    <br />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      {previewSrc ? (
                        <>
                          <img
                            src={previewSrc}
                            alt={props.t("Image de la publicité")}
                            style={{ maxWidth: "50%" }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = defaultPublicityImage;
                            }}
                          />
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted fs-12">
                              {selectedFile
                                ? props.t("Nouvelle image sélectionnée")
                                : props.t("Image actuelle")}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={handleRemoveImage}
                            >
                              <i className="ri-delete-bin-line align-bottom me-1"></i>
                              {props.t("Retirer l'image")}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <img
                            src={defaultPublicityImage}
                            alt={props.t("Aucune image")}
                            style={{ maxWidth: "50%", opacity: 0.5 }}
                          />
                          <span className="text-muted fs-12">
                            {props.t("Aucune image ne sera enregistrée")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <br />
                </div>
                <div className="mb-3">
                  <Label className="form-label" htmlFor="title">
                    {props.t("TITRE")} <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    type="text"
                    className="form"
                    style={{ fontWeight: "bold", textTransform: "uppercase" }}
                    name="title"
                    value={validation.values.title}
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    invalid={
                      validation.errors.title && validation.touched.title
                        ? true
                        : false
                    }
                  />
                  {validation.errors.title && validation.touched.title ? (
                    <FormFeedback type="invalid">
                      {props.t(validation.errors.title)}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label className="form-label" htmlFor="publicty_content">
                    {" "}
                    {props.t("CONTENU")} <span style={{ color: "red" }}>*</span>
                  </Label>
                  <textarea
                    className="form-control"
                    name="publicity_content"
                    value={validation.values.publicity_content}
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    rows="10"
                    cols="50"
                  />
                  {validation.errors.publicity_content &&
                  validation.touched.publicity_content ? (
                    <FormFeedback type="invalid">
                      {props.t(validation.errors.publicity_content)}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label className="form-label" htmlFor="priority">
                    {props.t("PRIORITE DE LA PUBLICITE")}
                  </Label>
                  <Input
                    type="number"
                    className="form"
                    name="priority"
                    value={validation.values.priority}
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    min={1}
                    max={5}
                    invalid={
                      validation.errors.priority && validation.touched.priority
                        ? true
                        : false
                    }
                  />
                  {validation.errors.priority && validation.touched.priority ? (
                    <FormFeedback type="invalid">
                      {props.t(validation.errors.priority)}
                    </FormFeedback>
                  ) : null}
                  {/* <div style={{ display: 'flex', alignItems: 'center',justifyItems:'center' }}>
                      <button type="button" onClick={()=> setCount(selectAticle.priority - 1) } style={{ marginRight:'20px' }}>-</button>
                      <Input
                        type="number"
                        className="form"
                        name="priority"
                        style={{ textAlign: 'center' }}
                        value={count}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                            setCount(newValue);
                        }}

                      />
                       <button type="button" onClick={()=> setCount(selectAticle.priority + 1)} style={{ marginLeft:'20px' }}>+</button>
                       </div> */}
                </div>
              </Card>
              <div className="text-end mb-3">
                <Link to="/advertisements" className="btn btn-primary me-2">
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning m-2"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending && (
                    <Spinner size="sm" className="me-2" />
                  )}
                  {props.t("Modifier")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};
PublicitiesUpdate.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(PublicitiesUpdate));
